import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
  const [favorites, setFavorites] = useState([]);
  const [kids, setKidsState] = useState(() => localStorage.getItem("cf_kids") === "1");
  const [activeProfile, setProfileState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ntv_profile") || "null"); } catch { return null; }
  });

  const setKids = (v) => { localStorage.setItem("cf_kids", v ? "1" : "0"); setKidsState(v); };
  const setActiveProfile = (p) => {
    localStorage.setItem("ntv_profile", JSON.stringify(p));
    setProfileState(p);
    localStorage.setItem("cf_kids", p?.is_kids ? "1" : "0");
    setKidsState(!!p?.is_kids);
  };

  const loadFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/me/list");
      setFavorites(data.items.map((i) => i.id));
    } catch {
      setFavorites([]);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("cf_token");
    if (!token) { setUser(false); return; }
    api.get("/auth/me")
      .then(({ data }) => { setUser(data); loadFavorites(); })
      .catch(() => { localStorage.removeItem("cf_token"); setUser(false); });
  }, [loadFavorites]);

  const authenticate = (token, u) => {
    localStorage.setItem("cf_token", token);
    setUser(u);
    loadFavorites();
    const ref = localStorage.getItem("ntv_ref");
    if (ref) api.post("/me/referral", { code: ref }).catch(() => {}).finally(() => localStorage.removeItem("ntv_ref"));
  };

  const refreshUser = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; }
    catch { return null; }
  }, []);

  const logout = () => {
    localStorage.removeItem("cf_token");
    localStorage.removeItem("ntv_profile");
    setProfileState(null);
    setUser(false);
    setFavorites([]);
  };

  const toggleFavorite = async (id) => {
    if (!user) return false;
    if (favorites.includes(id)) {
      setFavorites((f) => f.filter((x) => x !== id));
      await api.delete(`/me/list/${id}`);
      return false;
    }
    setFavorites((f) => [...f, id]);
    await api.post(`/me/list/${id}`);
    return true;
  };

  return (
    <AuthContext.Provider value={{
      user, favorites, kids, setKids,
      activeProfile, setActiveProfile,
      subscribed: !!user?.subscribed,
      paid: !!user?.paid,
      onTrial: !!user?.on_trial,
      accessUntil: user?.access_until || null,
      planTier: user?.tier || null,
      authenticate, logout, refreshUser, toggleFavorite,
      isFavorite: (id) => favorites.includes(id),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
